/**
 * Key people (KYB): the company's UBOs, directors and representatives, and the
 * awaiting screen while each completes their own KYC. Names and emails go to
 * the server, which seals them; the case context never holds them.
 */
import { apiUrl, authHeaders, CollectionFlowError } from './client';

export const KEY_PEOPLE_EVENT = 'KEY_PEOPLE_SUBMITTED';
/** Fired by the platform only; its presence marks the awaiting screen. */
export const KEY_PEOPLE_DONE_EVENT = 'KEY_PEOPLE_DONE';

export const ROLES = ['ubo', 'shareholder', 'director', 'representative', 'secretary'] as const;
export type Role = (typeof ROLES)[number];

export interface KeyPeopleView {
  kind: 'key_people';
  ubo_threshold: number;
  roles: Role[];
  kyc_roles: Role[];
  allow_unidentifiable_ubos: boolean;
}

export interface PersonDraft {
  name: string;
  email: string;
  nationality: string;
  roles: Role[];
  /** Kept as typed; sent as a number when present. */
  ownership: string;
}

export interface KeyPerson {
  name: string | null;
  roles: Role[];
  ownership_percent: number | null;
  nationality: string;
  kyc_status: 'approved' | 'not_required' | 'pending' | 'rejected' | 'review';
  link: string | null;
}

export function keyPeopleStepOf(step: unknown): KeyPeopleView | null {
  if (typeof step !== 'object' || step === null) return null;
  const s = step as Partial<KeyPeopleView>;
  return s.kind === 'key_people' && typeof s.ubo_threshold === 'number'
    ? (s as KeyPeopleView)
    : null;
}

export function isAwaitingStep(step: unknown): boolean {
  return (
    typeof step === 'object' &&
    step !== null &&
    (step as { kind?: unknown }).kind === 'awaiting_users'
  );
}

export const emptyPerson = (): PersonDraft => ({
  name: '',
  email: '',
  nationality: '',
  roles: [],
  ownership: '',
});

/** What a person still lacks before the declaration can be sent. */
export function personProblem(p: PersonDraft, view: KeyPeopleView): string | null {
  if (!p.name.trim()) return 'name';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email.trim())) return 'email';
  if (!/^[A-Z]{2}$/.test(p.nationality)) return 'nationality';
  if (p.roles.length === 0) return 'roles';
  if (p.roles.includes('ubo')) {
    const pct = Number(p.ownership);
    if (
      p.ownership.trim() === '' ||
      !Number.isFinite(pct) ||
      pct < view.ubo_threshold ||
      pct > 100
    ) {
      return 'ownership';
    }
  }
  return null;
}

/** The server's `key_people::Declaration`. */
export function declarationOf(people: PersonDraft[], ubosUnidentifiable: boolean) {
  return {
    people: people.map((p) => ({
      name: p.name.trim(),
      email: p.email.trim(),
      nationality: p.nationality,
      roles: p.roles,
      ...(p.ownership.trim() === '' ? {} : { ownership_percent: Number(p.ownership) }),
    })),
    ubos_unidentifiable: ubosUnidentifiable,
  };
}

export async function listKeyPeople(token: string): Promise<KeyPerson[]> {
  const response = await fetch(apiUrl('/api/v1/collection-flow/key-people'), {
    headers: authHeaders(token),
  });
  if (!response.ok) throw new CollectionFlowError(response.status, 'collection-flow/key-people');
  return (await response.json()) as KeyPerson[];
}

/**
 * A key person's closing screen: the company they verified for, and where its
 * verification is. Never a verdict — the company's outcome is the partner's to
 * tell. `null` for every case that is not a key person's KYC.
 */
export type CompanyStage = 'in_progress' | 'awaiting_people' | 'finished';

export interface CompanyProgress {
  company_name: string | null;
  stage: CompanyStage;
}

export async function getCompanyProgress(token: string): Promise<CompanyProgress | null> {
  const response = await fetch(apiUrl('/api/v1/collection-flow/company'), {
    headers: authHeaders(token),
  });
  if (!response.ok) throw new CollectionFlowError(response.status, 'collection-flow/company');
  return (await response.json()) as CompanyProgress | null;
}
